import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageComponent } from '../../../../../services/website.service';

interface FormFieldDefinition {
  id: string;
  label: string;
  name: string;
  type: 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'checkbox';
  required: boolean;
  placeholder: string;
  options: string[];
  width: 'full' | 'half';
}

@Component({
  selector: 'app-form-component',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './form-component.component.html',
  styleUrls: ['./form-component.component.scss']
})
export class FormComponentComponent {
  @Input() component!: PageComponent;
  @Input() mode: 'preview' | 'edit' = 'preview';
  @Input() isSelected: boolean = false;
  @Output() contentChange = new EventEmitter<void>();
  @Output() styleChange = new EventEmitter<void>();

  fieldTypes = [
    { value: 'text', label: 'Tekst' },
    { value: 'email', label: 'E-mail' },
    { value: 'tel', label: 'Telefoon' },
    { value: 'textarea', label: 'Tekstvak' },
    { value: 'select', label: 'Dropdown' },
    { value: 'checkbox', label: 'Checkbox' },
  ];

  // Getters
  getHeading(): string { return this.component.data['heading'] || ''; }
  getDescription(): string { return this.component.data['description'] || ''; }
  getSubmitButtonText(): string { return this.component.data['submitButtonText'] || 'Versturen'; }
  getSuccessMessage(): string { return this.component.data['successMessage'] || 'Bedankt voor uw bericht!'; }
  getFields(): FormFieldDefinition[] { return this.component.data['fields'] || []; }

  // Styling getters (same pattern as other components)
  getBackgroundType(): string { return this.component.data['backgroundType'] || 'solid'; }
  getBackgroundColor(): string { return this.component.data['backgroundColor'] || '#ffffff'; }
  getGradientFrom(): string { return this.component.data['gradientFrom'] || '#ffffff'; }
  getGradientTo(): string { return this.component.data['gradientTo'] || '#ffffff'; }
  getBackgroundImage(): string { return this.component.data['backgroundImage'] || ''; }
  getHeadingColor(): string { return this.component.data['headingColor'] || '#111827'; }
  getTextColor(): string { return this.component.data['textColor'] || '#374151'; }
  getTitlePosition(): string { return this.component.data['titlePosition'] || 'left'; }
  getParallax(): boolean { return this.component.data['parallax'] || false; }
  getSectionHeight(): string { return this.component.data['sectionHeight'] || ''; }
  getHeightUnit(): string { return this.component.data['heightUnit'] || 'px'; }

  getPreviewStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    const backgroundType = this.getBackgroundType();

    if (backgroundType === 'solid') {
      styles['background-color'] = this.getBackgroundColor();
    } else if (backgroundType === 'gradient') {
      styles['background'] = `linear-gradient(to bottom, ${this.getGradientFrom()}, ${this.getGradientTo()})`;
    } else if (backgroundType === 'image') {
      const bgImage = this.getBackgroundImage();
      if (bgImage) {
        styles['background-image'] = `url(${bgImage})`;
        styles['background-size'] = 'cover';
        styles['background-position'] = 'center';
      }
    }

    const sectionHeight = this.getSectionHeight();
    if (sectionHeight) {
      styles['min-height'] = `${sectionHeight}${this.getHeightUnit()}`;
    }

    return styles;
  }

  getTitlePositionClass(): string {
    const position = this.getTitlePosition();
    switch (position) {
      case 'center': return 'text-center';
      case 'right': return 'text-right';
      default: return 'text-left';
    }
  }

  getFieldTypeLabel(type: string): string {
    const found = this.fieldTypes.find(t => t.value === type);
    return found ? found.label : type;
  }
}
